<ng-container *ngIf="currentUserWrapper$ | async as currentUserWrapper">
  <div
    [class.highlighted]="highlighted"
    [ngStyle]="{ 'margin-left': margin }"
    id="c{{ comment.id }}"
    class="nested-comment"
  >
    <div class="avatar">
      <img [src]="avatarUrl" alt="" />
    </div>

    <div class="nested-comment-content">
      <div class="meta d-flex justify-content-between align-items-center">
        <div>
          <a
            *ngIf="!comment.deleted"
            [href]="userGalleryUrl"
            (click)="
              userService.openGallery(
                comment.authorUsername,
                !currentUserWrapper.userProfile || currentUserWrapper.userProfile.enableNewGalleryExperience
              )
            "
            class="username"
            astrobinEventPreventDefault
          >
            {{ comment.authorDisplayName }}
          </a>

          <span *ngIf="comment.deleted" class="username">
            {{ "(deleted)" | translate }}
          </span>

          <span [attr.data-test-value]="comment.created" class="timestamp">
            {{ comment.created | localDate | timeago }}
          </span>

          <astrobin-toggle-property
            *ngIf="!comment.pendingModeration && !comment.deleted && commentContentType"
            [contentType]="commentContentType.id"
            [count]="comment.likes.length"
            [disabled]="!currentUserWrapper.user?.id || currentUserWrapper.user.id === comment.author"
            [objectId]="comment.id"
            [showLabel]="false"
            [toggled]="comment.likes.includes(currentUserWrapper.user?.id)"
            [userId]="currentUserWrapper.user?.id"
            class="btn-no-block"
            btnClass="btn btn-sm btn-link link-secondary"
            propertyType="like"
          ></astrobin-toggle-property>
        </div>

        <!-- Only show dropdown if comment is not deleted -->
        <div *ngIf="!comment.deleted" class="d-inline-block py-0" ngbDropdown>
          <button
            [attr.id]="'dropdown-comment-' + comment.id"
            class="btn btn-link btn-sm btn-no-block text-muted no-toggle"
            ngbDropdownToggle
          >
            <fa-icon icon="ellipsis-v"></fa-icon>
          </button>
          <div [attr.aria-labelledby]="'dropdown-comment-' + comment.id" ngbDropdownMenu>
            <button
              *ngIf="!comment.pendingModeration && !comment.deleted"
              (click)="onShareClicked($event)"
              class="dropdown-item"
            >
              {{ "Share" | translate }}
            </button>

            <button
              *ngIf="!comment.deleted && currentUserWrapper.user?.id === comment.author"
              [hidden]="showReplyForm"
              (click)="onEditClicked($event)"
              class="dropdown-item"
            >
              {{ "Edit" | translate }}
            </button>

            <button
              *ngIf="!comment.deleted && currentUserWrapper.user?.id && currentUserWrapper.user?.id !== comment.author"
              (click)="onReportClicked($event)"
              class="dropdown-item"
            >
              {{ "Report abuse" | translate }}
            </button>
          </div>
        </div>
      </div>

      <div *ngIf="!comment.pendingModeration" [class.translated-content]="translated" class="text">
        <small *ngIf="translated" class="text-muted fst-italic d-block mb-2">{{ "Translated" | translate }}</small>
        <div [innerHTML]="html"></div>
      </div>

      <div *ngIf="comment.pendingModeration" class="text">
        <p class="alert alert-dark alert-mini w-100">
          <fa-icon icon="info-circle"></fa-icon>
          {{ "This comment is pending moderation." | translate }}
        </p>
        <div *ngIf="allowModeration">
          <p [innerHTML]="html" class="text"></p>
          <button
            [class.loading]="approving"
            (click)="onApproveClicked($event)"
            class="btn btn-outline-light btn-xs btn-no-block me-2"
          >
            {{ "Approve" | translate }}
          </button>
          <button
            [class.loading]="loadingService.loading$ | async"
            (click)="onDeleteClicked($event)"
            class="btn btn-outline-danger btn-xs btn-no-block"
          >
            {{ "Delete" | translate }}
          </button>
        </div>
      </div>

      <div class="actions">
        <button
          *ngIf="
            !translated &&
            !!currentUserWrapper.user &&
            comment.detectedLanguage &&
            comment.detectedLanguage !== translateService.currentLang
          "
          [class.loading]="translating"
          (click)="onTranslateClicked($event)"
          class="btn btn-link btn-sm btn-no-block text-muted translate"
        >
          <fa-icon icon="language"></fa-icon>
          {{ "Translate" | translate }}
        </button>

        <button
          *ngIf="translated"
          (click)="onSeeOriginalClicked($event)"
          class="btn btn-link btn-sm btn-no-block text-muted translated"
        >
          <fa-icon icon="language"></fa-icon>
          {{ "See original" | translate }}
        </button>

        <button
          *ngIf="
            !comment.pendingModeration &&
            !comment.deleted &&
            showReplyButton &&
            ((currentUserWrapper.user?.id !== comment.author &&
              (!restrictReplyToUserId || (restrictReplyToUserId && restrictReplyToUserId === comment.author))) ||
              (currentUserWrapper.user?.id === comment.author && allowSelfReply))
          "
          [hidden]="showReplyForm"
          (click)="onReplyClicked($event)"
          class="btn btn-sm btn-no-block btn-link reply"
        >
          {{ "Reply" | translate }}
        </button>
      </div>

      <div *ngIf="showEditForm" class="edit-form">
        <astrobin-read-only-mode
          *ngIf="(readOnlyMode$ | async) === true; else editFormTemplate"
        ></astrobin-read-only-mode>

        <ng-template #editFormTemplate>
          <form [formGroup]="editForm" class="d-block mt-3">
            <formly-form [fields]="editFields" [form]="editForm" [model]="editModel"></formly-form>
          </form>

          <button
            [class.loading]="loadingService.loading$ | async"
            (click)="cancelEdit()"
            class="btn btn-sm btn-outline-secondary mt-3"
          >
            {{ "Cancel" | translate }}
          </button>

          <button
            [class.loading]="loadingService.loading$ | async"
            [disabled]="!editForm.valid"
            (click)="submitEdit()"
            class="btn btn-primary btn-sm ms-sm-2 mt-3"
          >
            {{ "Submit" | translate }}
          </button>
        </ng-template>
      </div>

      <div *ngIf="showReplyForm" class="reply-form">
        <astrobin-read-only-mode
          *ngIf="(readOnlyMode$ | async) === true; else replyFormTemplate"
        ></astrobin-read-only-mode>

        <ng-template #replyFormTemplate>
          <form [formGroup]="replyForm" class="d-block mt-3">
            <formly-form [fields]="replyFields" [form]="replyForm" [model]="replyModel"></formly-form>
          </form>

          <button
            [class.loading]="loadingService.loading$ | async"
            (click)="cancelReply()"
            class="btn btn-sm btn-outline-secondary mt-3"
          >
            {{ "Cancel" | translate }}
          </button>

          <button
            [class.loading]="loadingService.loading$ | async"
            [disabled]="!replyForm.valid"
            (click)="submitReply()"
            class="btn btn-primary btn-sm ms-sm-2 mt-3"
          >
            {{ "Submit" | translate }}
          </button>
        </ng-template>
      </div>
    </div>
  </div>

  <!-- Report Abuse Modal -->
  <astrobin-report-abuse-modal #reportAbuseModal></astrobin-report-abuse-modal>

  <!-- Reply Offcanvas Template -->
  <ng-template #replyOffcanvasTemplate let-offcanvas>
    <div class="offcanvas-header">
      <h4 class="offcanvas-title">{{ "Reply to comment" | translate }}</h4>
      <button (click)="offcanvas.dismiss()" class="btn-close" aria-label="Close" type="button"></button>
    </div>
    <div class="offcanvas-body">
      <astrobin-read-only-mode
        *ngIf="(readOnlyMode$ | async) === true; else replyOffcanvasFormTemplate"
      ></astrobin-read-only-mode>

      <ng-template #replyOffcanvasFormTemplate>
        <!-- Original Comment Display -->
        <div class="original-comment mb-3">
          <div class="d-flex align-items-start">
            <div class="avatar me-3">
              <img [src]="avatarUrl" alt="" />
            </div>
            <div class="comment-content flex-grow-1">
              <div class="meta mb-2">
                <span class="username">{{ comment.authorDisplayName }}</span>
                <span class="timestamp ms-2">{{ comment.created | localDate | timeago }}</span>
              </div>
              <div [innerHTML]="html" class="text"></div>
            </div>
          </div>
        </div>

        <!-- Reply Form -->
        <div class="reply-form-section">
          <form [formGroup]="replyForm" class="d-block">
            <!-- Minimal layout -->
            <div *ngIf="!expandedOffcanvasEditor" class="mobile-reply-controls d-flex align-items-center gap-2">
              <div class="mobile-input-wrapper flex-grow-1">
                <formly-form [fields]="replyOffcanvasFields" [form]="replyForm" [model]="replyModel"> </formly-form>
                <button
                  [title]="'Expand editor' | translate"
                  (click)="toggleExpandedOffcanvasEditor()"
                  class="btn btn-link btn-no-block expand-btn"
                  type="button"
                >
                  <fa-icon icon="expand"></fa-icon>
                </button>
              </div>
              <button
                [class.loading]="_isSubmittingReply"
                [disabled]="!replyForm.valid || _isSubmittingReply"
                (click)="submitReplyOffcanvas()"
                class="btn btn-primary btn-mobile-send"
                type="submit"
              >
                <fa-icon icon="paper-plane"></fa-icon>
              </button>
            </div>

            <!-- Expanded layout -->
            <div *ngIf="expandedOffcanvasEditor">
              <div class="d-flex justify-content-end mb-2">
                <button
                  (click)="toggleExpandedOffcanvasEditor()"
                  class="btn btn-link btn-sm btn-no-block text-muted"
                  type="button"
                >
                  <fa-icon icon="compress"></fa-icon>
                  {{ "Minimal editor" | translate }}
                </button>
              </div>
              <formly-form [fields]="replyOffcanvasFields" [form]="replyForm" [model]="replyModel"> </formly-form>
              <div class="d-flex justify-content-end gap-2 mt-3">
                <button
                  [class.loading]="_isSubmittingReply"
                  [disabled]="!replyForm.valid || _isSubmittingReply"
                  (click)="submitReplyOffcanvas()"
                  class="btn btn-primary"
                  type="submit"
                >
                  <fa-icon icon="paper-plane"></fa-icon>
                  {{ "Send" | translate }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ng-template>
    </div>
  </ng-template>
</ng-container>
